/**
 * Analytics, under Google Consent Mode v2.
 *
 * The tag loads on every visit, but it starts denied. Before anyone opts in,
 * `analytics_storage` is `denied`, so gtag sets no cookies and stores no
 * identifier; opting in sends a consent *update* and the tag begins behaving
 * normally. Opting back out sends another update returning it to denied.
 *
 * This replaces an earlier design that injected the tag only after a yes. That
 * was the stronger privacy position and it is the one we would still prefer, but
 * it made the tag undetectable to Google's installation check, because the tag
 * genuinely was not in the page until a human clicked a button. Shay's call, made
 * with the trade-off stated: see D-087.
 *
 * The trade-off, plainly: Google's servers now receive a request — and therefore
 * an IP address — from every visitor, including those who decline. The consent
 * state governs identifiers and cookies, not whether contact happens at all. The
 * UI says so rather than implying otherwise.
 *
 * What Google never gets, in either state: the photos, their labels, their types,
 * or anything derived from them.
 */

import { meta, setMeta } from './store.ts';

export const MEASUREMENT_ID = 'G-V03MZ70R57';
export type Consent = 'granted' | 'denied' | 'unset';

let started = false;

export async function consent(): Promise<Consent> {
  return (await meta<Consent>('analyticsConsent')) ?? 'unset';
}

export async function setConsent(value: Exclude<Consent, 'unset'>): Promise<void> {
  await setMeta('analyticsConsent', value);
  update(value);
  if (value === 'denied') forgetGoogleCookies();
}

/**
 * Called at boot, before the first render. Loads the tag with consent defaulted
 * to denied, then applies a stored answer if there is one.
 */
export async function initAnalytics(): Promise<void> {
  const stored = await consent();
  start();
  if (stored === 'granted') update('granted');
}

declare global {
  interface Window { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void }
}

/**
 * The official snippet pushes the `arguments` object itself, not an array, and
 * gtag.js tells the two apart. Pushing `[...args]` here looks equivalent and is
 * not — it is why a "correctly installed" tag can sit there reporting nothing.
 * Do not simplify this to `push(args)`.
 */
function gtag(..._args: unknown[]): void {
  window.dataLayer!.push(arguments);
}

function start(): void {
  if (started || document.getElementById('ga-tag')) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  // Order matters more than anything else in this file. The default must be in
  // the dataLayer before the tag is fetched, or gtag runs briefly in its default
  // granted state and sets a cookie before we ever say not to.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  // Strips identifiers from anything advertising-related while ad_storage is
  // denied. We run no ads tag, so this is belt and braces.
  gtag('set', 'ads_data_redaction', true);
  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID);

  const script = document.createElement('script');
  script.id = 'ga-tag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.append(script);
}

/** Analytics only. Advertising storage stays denied whatever the answer is. */
function update(value: Exclude<Consent, 'unset'>): void {
  if (!started) start();
  gtag('consent', 'update', { analytics_storage: value });
}

/**
 * Withdrawing consent returns the tag to denied for the rest of the session and
 * clears what it stored. It cannot retract a measurement already sent, and the
 * settings copy says so rather than implying otherwise.
 */
function forgetGoogleCookies(): void {
  const names = document.cookie.split(';')
    .map((c) => c.split('=')[0]?.trim())
    .filter((n): n is string => !!n && (n === '_ga' || n.startsWith('_ga_') || n === '_gid'));
  for (const name of names) {
    for (const domain of [location.hostname, `.${location.hostname}`]) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    }
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}
